/**
 * InputDock — fixed-bottom glass-card chat input.
 *
 * Renders in conversation mode. Visually mirrors the homepage
 * `HomeSearchInput.tsx` glass card so users feel they're on the
 * same Mio surface — not a "secondary" chat input.
 *
 * Visual contract (locked 2026-06-15, mockup v4):
 *   - position: fixed; bottom: 0; full-width
 *   - 720px max-width inner card centred
 *   - 20px radius, blur(24px), 1px border with amber on focus
 *   - 6px outer padding + 24px left padding for the ✦ icon
 *   - 52px amber gradient send button
 *   - subtle gradient fade above the dock so thread messages don't
 *     read as cut off where they slide under the dock
 *   - placeholder reminds user typing pivots / refines / changes topic
 *
 * Day 7 polish (2026-06-15):
 *   - Bottom padding honours `env(safe-area-inset-bottom)` so the
 *     dock sits above the iPhone home indicator without overlap
 *   - focus-visible ring on the send button
 *
 * Day 10 accessibility (2026-06-15):
 *   - Auto-focus the input on mount so when the user submits from
 *     HeroPanel the focus lands here naturally (no extra click)
 *   - aria-describedby links the hint paragraph to the input
 *   - Send button label distinguishes empty (disabled, "Send") from
 *     sending (disabled, "Sending")
 *
 * The dock is the PRIMARY interaction. Chips above it are shortcuts.
 * Anything typed here wins over a chip click.
 */

"use client";

import { type FormEvent, useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";

export interface InputDockProps {
  /** Called when the user submits. Input clears after submit fires. */
  onSubmit: (text: string) => void;
  /** Set true while a turn is in flight to disable the input. */
  disabled?: boolean;
  /** Optional placeholder override. */
  placeholder?: string;
}

// Placeholder hint signals BOTH paths to the user:
//   1) Refine the conversation (type more detail)
//   2) Search a specific product instantly (type brand + model + variant
//      and the agent fires search on submit)
const DEFAULT_PLACEHOLDER =
  "type a specific product to search, or refine";
const HINT_ID = "agent-input-dock-hint";

export function InputDock({
  onSubmit,
  disabled = false,
  placeholder = DEFAULT_PLACEHOLDER,
}: InputDockProps) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Day 10: auto-focus on mount. Day 5 wired the InputDock to render
  // only after the first user submit triggers idle → conversation
  // mode. At that moment focus is on the HeroPanel input which is
  // about to unmount. Moving focus here keeps the keyboard caret
  // alive and avoids forcing the user to tap/click before continuing.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue("");
  }

  // Disabled-button semantics:
  //   value empty + not disabled  → "Send" (still disabled at the
  //                                  HTML level so screen readers
  //                                  see disabled state but the
  //                                  label is the intent)
  //   disabled (turn in flight)   → "Sending"
  const sendLabel = disabled ? "Sending" : "Send";

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-30"
      style={{
        bottom: 0,
        background:
          "linear-gradient(to top, rgba(10,9,8,0.96) 40%, rgba(10,9,8,0) 100%)",
        padding:
          "16px 24px calc(20px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="pointer-events-auto mx-auto"
        style={{ maxWidth: 720 }}
      >
        <div
          className={cn(
            "flex items-center gap-3",
            "border bg-white/[0.05] backdrop-blur-2xl",
            "transition-shadow duration-200",
          )}
          style={{
            borderColor: "rgba(255,255,255,0.12)",
            borderRadius: 20,
            padding: "6px 6px 6px 24px",
            boxShadow:
              "0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.04)",
          }}
        >
          <span
            aria-hidden="true"
            className="flex-shrink-0"
            style={{ fontSize: "1.25rem", lineHeight: 1, color: "#f4a93c" }}
          >
            ✦
          </span>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            enterKeyHint="send"
            aria-describedby={HINT_ID}
            className={cn(
              "flex-1 bg-transparent text-foreground",
              "outline-none focus-visible:outline-none",
              "disabled:opacity-50",
            )}
            style={{
              fontSize: "1.0625rem",
              padding: "16px 4px",
              letterSpacing: "-0.005em",
              color: "hsl(var(--foreground))",
              minWidth: 0,
            }}
            aria-label="Type your message to Octopusden"
          />
          <button
            type="submit"
            disabled={!value.trim() || disabled}
            className={cn(
              "flex flex-shrink-0 items-center justify-center font-bold",
              "transition-transform duration-150",
              "hover:scale-[1.04] active:scale-[0.97]",
              "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "focus-visible:ring-offset-[#0a0908]",
            )}
            style={{
              background:
                "linear-gradient(135deg, #fde68a 0%, #fbbf24 100%)",
              color: "#0a0908",
              width: 52,
              height: 52,
              borderRadius: 14,
              boxShadow: "0 6px 16px rgba(251,191,36,0.3)",
              ["--tw-ring-color" as string]: "#fbbf24",
            }}
            aria-label={sendLabel}
          >
            {disabled ? (
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              />
            ) : (
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            )}
          </button>
        </div>
        <p
          id={HINT_ID}
          className="mt-2 text-center"
          style={{
            fontSize: "var(--fs-micro)",
            color: "hsl(var(--muted-foreground))",
          }}
        >
          Type the exact product to search (e.g. "iPhone 17 Pro Max 256GB"),
          or refine your last answer.
        </p>
      </form>
    </div>
  );
}
